import dayjs from 'dayjs';
import { SeatingStateHelper } from './seating-state-helper.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { SeatObjectState } from '../models/serializable/seating-state/seat-object-state.js';
import { ArtificialSeatSelectionEntry, ISeatSelectionEntry, PassSeatSelectionEntry, PermanentSeatSelectionEntry, ReservedSeatSelectionEntry, TemporarySeatSelectionEntry } from '../models/serializable/ticketing-state/seat-selection-entry.js';
import { EventSeatingState } from '../models/serializable/seating-state/event-seating-state.js';
import { EventSeatSelectionLock } from '../models/serializable/ticketing-state/event-seat-selection-lock.js';
import { ITicketPool } from '../models/serializable/ticketing-state/ticket-pool.js';
import { Seat } from '../models/serializable/seating/seat.js';
import { SeatInfo } from '../models/serializable/seating-state/seat-info.js';
import { SeatState } from '../models/serializable/seating-state/seat-state.js';
import { SeatOrderType } from '../models/serializable/seating-state/seat-order-type.js';

export class SeatStateStreamSnapshot {
  private readonly seatLocks: Map<number, ISeatSelectionEntry[]>;
  private readonly reservedSeats: ReadonlySet<number>;
  private readonly helper: SeatingStateHelper | null;

  constructor(
      seatingState: EventSeatingState | null,
      seatSelectionLock: EventSeatSelectionLock | null,
      ticketPool: ITicketPool | null,
      currentTime: dayjs.Dayjs,
  ) {
    this.seatLocks = new Map<number, ISeatSelectionEntry[]>();
    this.helper = isNotNullOrUndefined(seatingState) ? new SeatingStateHelper(seatingState) : null;

    if(isNotNullOrUndefined(seatSelectionLock)) {
      for(const entry of seatSelectionLock.entries) {
        if(entry.isExpired(currentTime)) {
          continue;
        }

        if(entry instanceof ArtificialSeatSelectionEntry) {
          continue;
        }

        for(const seat of entry.seats) {
          const entries = this.seatLocks.get(seat) ?? new Array<ISeatSelectionEntry>();
          entries.push(entry);
          this.seatLocks.set(seat, entries);
        }
      }
    }

    const seatingModule = ticketPool?.getSeatingModule();
    if(isNotNullOrUndefined(seatingModule)) {
      this.reservedSeats = seatingModule.reservedSeats;
    }
    else {
      this.reservedSeats = new Set<number>();
    }
  }

  getStateForSeat(seat: Seat) : SeatObjectState {
    const isEnabled = this.isSeatEnabled(seat);
    if(isEnabled === false) {
      return SeatObjectState.Disabled;
    }

    const seatId = seat.uniqueId;
    const info = this.helper?.getSeatInfo(seatId);
    const state = this.getStateFromSeatInfo(info);
    if(state !== SeatObjectState.Free) {
      return state;
    }

    const locks = this.seatLocks.get(seatId);
    if(isNotNullOrUndefined(locks) && locks.length > 0) {
      for(const lock of locks) {
        if(lock instanceof TemporarySeatSelectionEntry) {
          return SeatObjectState.InCart;
        }
        else if(lock instanceof PermanentSeatSelectionEntry) {
          return SeatObjectState.Locked;
        }
        else if(lock instanceof ReservedSeatSelectionEntry) {
          return SeatObjectState.ReservedForTicket;
        }
        else if(lock instanceof PassSeatSelectionEntry) {
          if(lock.locks.includes(seatId)) {
            return SeatObjectState.LockedByPass;
          }
          else {
            return SeatObjectState.ReservedByPass;
          }
        }
      }

      return SeatObjectState.Locked;
    }

    if(this.reservedSeats.has(seatId)) {
      // here we would need a new "WaitingForPayment" state
      return SeatObjectState.InCart;
    }

    if(seat.tickets.length <= 0) {
      return SeatObjectState.NotConfigured;
    }

    return SeatObjectState.Free;
  }

  private isSeatEnabled(seat: Seat) : boolean {
    let isEnabled: boolean = seat.enabled;
    let parent = seat.parent?.deref();

    while(isNotNullOrUndefined(parent)) {
      isEnabled = isEnabled && parent.enabled;
      parent = parent.parent?.deref();
    }

    return isEnabled;
  }

  private getStateFromSeatInfo(info: SeatInfo | null | undefined) : SeatObjectState {
    if(isNullOrUndefined(info)) {
      return SeatObjectState.Free;
    }

    switch(info.state) {
      case SeatState.Free:
        return SeatObjectState.Free;
      case SeatState.Sold:
        return SeatObjectState.Sold;
      case SeatState.Taken:
        return this.getScannedStateFromSeatInfo(info);
      case SeatState.Reserved:
        return SeatObjectState.ReservedForTicket;
    }
  }

  private getScannedStateFromSeatInfo(info: SeatInfo): SeatObjectState {
    switch(info.orderType) {
      case SeatOrderType.None:
        return SeatObjectState.Scanned;
      case SeatOrderType.Ticket:
        return SeatObjectState.TicketScanned;
      case SeatOrderType.Pass:
        return SeatObjectState.PassScanned;
    }
  }
}
